import { useEffect, useRef, useCallback } from "react";
import { useDocumentStore } from "../stores/document";

interface UseAutoSaveOptions {
  /** Debounce time in milliseconds. Default: 2000 */
  debounceMs?: number;
  /** Whether auto-save is enabled. Default: true */
  enabled?: boolean;
}

/**
 * Auto-save hook. Watches unsavedChanges from the document store and triggers
 * saveDocument after a configurable debounce period following the last change.
 * Only saves when unsavedChanges is true; debounce resets when content changes
 * (tracked via currentDocument.content).
 *
 * Also registers a beforeunload handler and visibilitychange flush to prevent
 * data loss when the user closes the tab or navigates away.
 */
export function useAutoSave(options: UseAutoSaveOptions = {}) {
  const { debounceMs = 2000, enabled = true } = options;

  const unsavedChanges = useDocumentStore((s) => s.unsavedChanges);
  const content = useDocumentStore((s) => s.currentDocument?.content ?? "");
  const saveDocument = useDocumentStore((s) => s.saveDocument);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled || !unsavedChanges) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      saveDocument();
      timeoutRef.current = null;
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [content, unsavedChanges, saveDocument, debounceMs, enabled]);

  const handleBeforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      if (useDocumentStore.getState().unsavedChanges) {
        e.preventDefault();
      }
    },
    [],
  );

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === "hidden" && useDocumentStore.getState().unsavedChanges) {
      useDocumentStore.getState().saveDocument();
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, handleBeforeUnload, handleVisibilityChange]);
}
